import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { TransactionalButton } from '@bibliocommons/deprecated-base-buttons';
import { bibShape, libraryShape } from '@bibliocommons/bc-prop-types';

import { openAvailability } from 'app/actions/AvailabilityDetailActions';
import * as CheckoutActions from 'app/actions/CheckoutActions';
import * as HoldActions from 'app/actions/HoldActions';
import ModalActions from 'app/actions/ModalActions';
import ShelfActions from 'app/actions/ShelfActions';
import CancelHoldButton from 'app/components/shared/CancelHoldButton';
import { getItemTransactionButtonType, isDigitalReadyHold, TRANSACTION_BUTTON_TYPE } from 'app/helpers/bib/bibHelpers';
import { selectCurrentAccount } from 'app/selectors/AuthSelector';
import { selectActiveHoldsByMetadataId } from 'app/selectors/HoldsSelector';
import { selectCheckoutsByMetadataId } from 'app/selectors/CheckoutSelector';
import { selectBibEntities } from 'app/selectors/EntitiesSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import { trackCheckoutConsumeAccess, trackCheckoutConsumeDownload } from 'app/actions/AnalyticsGa4Actions';
import RequestItemButton from '../RequestItemButton';
import { CheckedOutReadingLinks } from '../ItemTransactions';

export function ItemTransactionButtonContainer(props) {
  const {
    currentLibrary,
    hold,
    isLoading,
    id,
    holdActions,
    checkoutActions,
    availableForCheckout,
    checkout,
    materialType,
    materialPolicy,
    accountId,
    format,
    title,
    authors,
    hasItemLevelHoldableItems,
    titleNotOwned,
    workflowId,
    messagingId,
    provider,
    bib,
    modalActions,
    openAvailability: dispatchOpenAvailability,
    shelfActions
  } = props;

  const transactionButtonType = getItemTransactionButtonType({
    bib: bib?.toJS(),
    checkout: checkout?.toJS(),
    hold: hold?.toJS(),
    library: currentLibrary?.toJS()
  });
  if (transactionButtonType === TRANSACTION_BUTTON_TYPE.NONE) {
    return null;
  }

  let element;
  switch (transactionButtonType) {
    case TRANSACTION_BUTTON_TYPE.CANCEL_HOLD:
      element = (
        <CancelHoldButton
          metadataId={id}
          cancelHold={() => holdActions.startCancelHold(workflowId || id, id, messagingId)}
          isLoading={isLoading}
        />
      );
      break;
    case TRANSACTION_BUTTON_TYPE.DIGITAL_OPTIONS:
      element = (
        <CheckedOutReadingLinks
          bib={bib}
          checkInEnabled={checkout.get('checkInEnabled')}
          downloadable={checkout.get('downloadable')}
          format={checkout.get('format')}
          downloadUrl={checkout.get('downloadUrl')}
          thirdPartyReaderUrl={checkout.get('thirdPartyReaderUrl')}
          policy={materialPolicy}
          provider={provider}
          metadataId={id}
          checkoutItem={checkout}
          messagingId={messagingId}
          checkoutId={checkout.get('checkoutId')}
          accountId={accountId}
          checkIn={checkoutActions.digitalCheckIn}
          startDigitalMissingFormatSelection={() =>
            checkoutActions.startDigitalMissingFormatSelection(workflowId || id, id, messagingId)}
          checkoutActions={checkoutActions}
          modalActions={modalActions}
          shelfActions={shelfActions}
          trackCheckoutConsumeAccess={props.trackCheckoutConsumeAccess}
          trackCheckoutConsumeDownload={props.trackCheckoutConsumeDownload}
        />
      );
      break;
    case TRANSACTION_BUTTON_TYPE.REQUEST_TO_VIEW:
      element = (
        <TransactionalButton
          block
          dataKey={`request-to-view_${bib.getIn(['availability', 'metadataId'])}`}
          handleClick={() => dispatchOpenAvailability(bib.getIn(['availability', 'metadataId']))}
        >
          <FormattedMessage defaultMessage="Request to view" id="request_to_view" />
        </TransactionalButton>
      );
      break;
    case TRANSACTION_BUTTON_TYPE.REQUEST_ITEM:
    default:
      element = (
        <RequestItemButton
          metadataId={id}
          handleHoldClick={() =>
            holdActions.startPlaceHold({ workflowId: workflowId || id, metadataId: id, messagingId })}
          isLoading={isLoading}
          availableForCheckout={availableForCheckout || isDigitalReadyHold({ bib, hold })}
          materialType={materialType}
          handleCheckoutClick={() => checkoutActions.startDigitalCheckout(workflowId || id, id, messagingId)}
          format={format}
          title={title}
          treatAsItemLevelHold={hasItemLevelHoldableItems && materialPolicy.get('itemLevelHoldsPredetermined')}
          authors={authors}
          serviceAvailable={materialPolicy.get('healthy')}
          titleNotOwned={titleNotOwned}
        />
      );
  }

  return <div className="item-transaction-button-wrap">{element}</div>;
}

ItemTransactionButtonContainer.propTypes = {
  currentLibrary: libraryShape.isRequired,
  hold: ImmutablePropTypes.mapContains({
    id: PropTypes.string,
    status: PropTypes.string
  }),
  workflowId: PropTypes.string,
  messagingId: PropTypes.string,
  provider: PropTypes.string,
  isLoading: PropTypes.bool,
  holdActions: PropTypes.objectOf(PropTypes.func).isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  materialPolicy: ImmutablePropTypes.map,
  materialType: PropTypes.string,
  checkout: ImmutablePropTypes.mapContains({
    id: PropTypes.string
  }),
  accountId: PropTypes.number,
  availableForCheckout: PropTypes.bool,
  id: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  authors: ImmutablePropTypes.listOf(PropTypes.string),
  hasItemLevelHoldableItems: PropTypes.bool,
  titleNotOwned: PropTypes.bool,
  bib: bibShape,
  modalActions: PropTypes.objectOf(PropTypes.func),
  openAvailability: PropTypes.func.isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func),
  trackCheckoutConsumeAccess: PropTypes.func,
  trackCheckoutConsumeDownload: PropTypes.func
};

ItemTransactionButtonContainer.displayName = 'ItemTransactionButtonContainer';

const mapStateToProps = (state, ownProps) => {
  const account = selectCurrentAccount(state, ownProps);
  const bib = selectBibEntities(state).get(ownProps.id);
  const policy = state.getIn(['entities', 'bibs', ownProps.id, 'policy'], Immutable.Map());
  const holds = selectActiveHoldsByMetadataId(state).get(ownProps.id, Immutable.List());
  const availabilityStatus = state.getIn(['entities', 'bibs', ownProps.id, 'availability', 'status']);
  const checkouts = selectCheckoutsByMetadataId(state).get(ownProps.id, Immutable.List());

  return {
    accountId: account && account.get('id'),
    bib,
    currentLibrary: selectCurrentLibrary(state),
    hold: holds.size === 1 ? holds.first() : null,
    isLoading: state.getIn(['ui', 'itemTransactions', ownProps.id, 'isLoading'], false),
    checkout: checkouts.first(),
    availableForCheckout: availabilityStatus === 'AVAILABLE',
    materialType: state.getIn(['entities', 'bibs', ownProps.id, 'policy', 'materialType']),
    materialPolicy: policy.get('materialPolicy', Immutable.Map()),
    provider: policy.get('provider'),
    format: bib.getIn(['briefInfo', 'format']),
    title: bib.getIn(['briefInfo', 'title']),
    authors: bib.getIn(['briefInfo', 'authors']),
    hasItemLevelHoldableItems: policy.get('itemLevelHoldableItems'),
    titleNotOwned: availabilityStatus === 'RESOURCE_NOT_FOUND'
  };
};

const mapDispatchToProps = dispatch => ({
  holdActions: bindActionCreators(HoldActions, dispatch),
  checkoutActions: bindActionCreators(CheckoutActions, dispatch),
  modalActions: bindActionCreators(ModalActions, dispatch),
  shelfActions: bindActionCreators(ShelfActions, dispatch),
  openAvailability: bindActionCreators(openAvailability, dispatch),
  trackCheckoutConsumeAccess: bindActionCreators(trackCheckoutConsumeAccess, dispatch),
  trackCheckoutConsumeDownload: bindActionCreators(trackCheckoutConsumeDownload, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(ItemTransactionButtonContainer);
